import React from "react";

/* ── helpers ──────────────────────────────────────────────────────────────── */

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function formatValue(question, value) {
  if (value === undefined || value === null || value === "") return "—";
  switch (question.type) {
    case "radio":
    case "select": {
      const opt = question.options?.find((o) => o.value === value);
      return opt?.label || String(value);
    }
    case "file":
      return `${value.name} · ${formatBytes(value.size)}`;
    default:
      return String(value);
  }
}

/* ── component ────────────────────────────────────────────────────────────── */

const ReviewModal = React.memo(function ReviewModal({
  open,
  sections,
  formData,
  primaryContact,
  submitting,
  error,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  const contactRows = [
    { label: "Nama Lengkap", value: primaryContact.nama_lengkap },
    { label: "NIM", value: primaryContact.nim },
    { label: "Email", value: primaryContact.email },
    { label: "Nomor WhatsApp", value: primaryContact.nomor_telepon },
  ];

  return (
    <div className="spss-modal-backdrop fixed inset-0 z-50 flex items-center justify-center px-6 py-10">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={submitting ? undefined : onCancel}
      />

      {/* panel */}
      <div className="spss-modal-panel relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/15 bg-[#1a1a2e] p-6 desktop:p-8 shadow-2xl">
        {/* header */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-clipboard-check text-white text-lg" />
          </div>
          <h2 className="text-lg font-bold font-sans text-white">
            Tinjau Sebelum Mengirim
          </h2>
        </div>
        <p className="text-white/50 font-sans text-sm mb-6">
          Periksa kembali data Anda sebelum pendaftaran dikirim. Pastikan semua
          informasi sudah benar.
        </p>

        {/* primary contact */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 mb-6">
          <h3 className="text-white/40 font-sans text-xs font-semibold uppercase tracking-wider mb-4">
            Informasi Utama
          </h3>
          <div className="grid grid-cols-1 desktop:grid-cols-2 gap-x-6 gap-y-4">
            {contactRows.map((row) => (
              <div key={row.label} className="flex flex-col gap-0.5 min-w-0">
                <span className="text-white/40 font-sans text-xs">
                  {row.label}
                </span>
                <span className="text-white font-sans text-sm font-medium break-words">
                  {row.value || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* grouped answers */}
        <div className="flex flex-col gap-6">
          {sections.map((section) => (
            <div key={section.key}>
              <h3 className="flex items-center gap-2 text-white/80 font-sans text-sm font-semibold mb-3">
                <i className={`${section.icon} text-white/40 text-xs`} />
                {section.title}
              </h3>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/[0.06] overflow-hidden">
                {section.questions.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-start justify-between gap-6 px-5 py-3"
                  >
                    <span className="text-white/50 font-sans text-xs flex-1 min-w-0">
                      {q.question}
                    </span>
                    <span className="text-white font-sans text-sm font-medium text-right break-words max-w-[60%]">
                      {formatValue(q, formData[q.id])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* submit error */}
        {error && (
          <div className="flex items-center gap-2 mt-6">
            <i className="fa-solid fa-circle-exclamation text-red text-xs" />
            <p className="text-red text-xs font-sans font-medium">{error}</p>
          </div>
        )}

        {/* actions */}
        <div className="flex flex-col-reverse desktop:flex-row items-stretch desktop:items-center justify-end gap-3 mt-8 pt-6 border-t border-white/10">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-sans text-sm font-semibold text-white/60
              hover:text-white hover:bg-white/10 transition-all duration-300
              disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="fa-solid fa-pen text-xs" />
            Kembali & Perbaiki
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-white text-midnight
              font-sans text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg
              disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed cursor-pointer"
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin text-xs" />
                Mengirim...
              </>
            ) : (
              <>
                Konfirmasi & Kirim
                <i className="fa-solid fa-paper-plane text-xs" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ReviewModal;
